/**
 * @sazeo/sdk — Product Catalog hooks
 *
 * TanStack Query wrappers for the product-catalog addon. All calls route
 * through the Edge Function at `/addons/product-catalog/*` which is guarded
 * by requireAddonEnabled() and enforces RLS via the caller's JWT.
 *
 * Paired spec: specs/CODEX_PRODUCT_CATALOG_ADDON_V2.md §8
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Shared types (local — avoid circular dep; schemas package owns the source)
// ---------------------------------------------------------------------------

export interface CatalogProductFilter {
  categoryId?: string;
  isActive?: boolean;
  search?: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const catalogKeys = {
  all: ['product-catalog'] as const,
  categories: () => [...catalogKeys.all, 'categories'] as const,
  products: (filter?: CatalogProductFilter) =>
    [...catalogKeys.all, 'products', filter ?? null] as const,
  product: (id: string) => [...catalogKeys.all, 'product', id] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

function buildFilterQuery(filter?: CatalogProductFilter): string {
  if (!filter) return '';
  const params = new URLSearchParams();
  if (filter.categoryId) params.set('category_id', filter.categoryId);
  if (filter.isActive !== undefined) params.set('is_active', String(filter.isActive));
  if (filter.search) params.set('search', filter.search);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function useCategories(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: catalogKeys.categories(),
    queryFn: () => apiFetch<unknown[]>(supabase, 'addons/product-catalog/categories'),
  });
}

export function useCreateCategory(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      description?: string;
      parentId?: string;
      position?: number;
    }) =>
      apiFetch<unknown>(supabase, 'addons/product-catalog/categories', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.categories() });
    },
  });
}

export function useUpdateCategory(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{
        name: string;
        slug: string;
        description: string;
        parentId: string | null;
        position: number;
      }>;
    }) =>
      apiFetch<unknown>(supabase, `addons/product-catalog/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.categories() });
    },
  });
}

export function useDeleteCategory(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/product-catalog/categories/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export function useProducts(supabase: SupabaseClientLike, filter?: CatalogProductFilter) {
  return useQuery({
    queryKey: catalogKeys.products(filter),
    queryFn: () =>
      apiFetch<unknown[]>(supabase, `addons/product-catalog/products${buildFilterQuery(filter)}`),
  });
}

export function useProduct(supabase: SupabaseClientLike, id: string) {
  return useQuery({
    queryKey: catalogKeys.product(id),
    queryFn: () => apiFetch<unknown>(supabase, `addons/product-catalog/products/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateProduct(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      categoryId?: string;
      description?: string;
      priceGel?: number;
      unit?: string;
      sku?: string;
      isActive?: boolean;
      position?: number;
      metadata?: Record<string, unknown>;
    }) =>
      apiFetch<unknown>(supabase, 'addons/product-catalog/products', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

export function useUpdateProduct(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{
        name: string;
        slug: string;
        categoryId: string | null;
        description: string;
        priceGel: number | null;
        unit: string | null;
        sku: string | null;
        isActive: boolean;
        position: number;
        metadata: Record<string, unknown>;
      }>;
    }) =>
      apiFetch<unknown>(supabase, `addons/product-catalog/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.product(vars.id) });
      void qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

export function useDeleteProduct(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/product-catalog/products/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.product(id) });
      void qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Product Images
// ---------------------------------------------------------------------------

export function useAddProductImage(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      input,
    }: {
      productId: string;
      input: {
        storageKey: string;
        url: string;
        position?: number;
        altText?: string;
      };
    }) =>
      apiFetch<unknown>(supabase, `addons/product-catalog/products/${productId}/images`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.product(vars.productId) });
    },
  });
}

export function useDeleteProductImage(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      imageId,
    }: {
      productId: string;
      imageId: string;
    }) =>
      apiFetch<void>(supabase, `addons/product-catalog/products/${productId}/images/${imageId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.product(vars.productId) });
    },
  });
}
